/** Placeholder for a value the backend returned as null. */
export const EMPTY_VALUE = "-";

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Short stamp for the inbox list: time today, date otherwise. */
export const formatListTime = (isoDate: string | null): string => {
  if (!isoDate) {
    return "";
  }

  const date = new Date(isoDate);
  const now = new Date();

  if (isSameDay(date, now)) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
};

/** Full stamp under a message bubble. */
export const formatMessageTime = (isoDate: string | null): string => {
  if (!isoDate) {
    return "";
  }

  return new Date(isoDate).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Date-only value for the context card. */
export const formatDate = (isoDate: string | null): string => {
  if (!isoDate) {
    return EMPTY_VALUE;
  }

  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateTime = (isoDate: string | null): string => {
  if (!isoDate) {
    return EMPTY_VALUE;
  }

  return formatMessageTime(isoDate);
};

export const formatFileSize = (bytes: number): string => {
  if (!bytes) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getInitials = (name: string | null, email: string): string => {
  const source = (name || email || "").trim();

  if (!source) {
    return "?";
  }

  const parts = source.split(/[\s.@_-]+/).filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const getDisplayName = (name: string | null, email: string): string =>
  name && name.trim() ? name : email;
